/**
 * Lost-Lead Resurrection — the agentic win-back loop time-triggers can't run.
 *
 * HubSpot-style automation can re-mail a closed-lost deal on a timer; it
 * cannot REASON about why the deal died. This module closes that gap:
 *
 *   1. {@link selectResurrectionCandidates} — a pure, honest filter over the
 *      deal book: closed-lost longer than the cooldown, still quiet (no email
 *      activity since the loss), and no NEW open deal already in flight on
 *      the account (an owner runs that relationship — we stay out).
 *   2. {@link draftResurrectionSequence} — asks the REAL Argo agent-worker
 *      (injected, structurally `argoWorker`) to compose a 3-step win-back
 *      grounded in the captured loss reason + record timeline. On worker
 *      error / timeout / absence it degrades to {@link deterministicDraft},
 *      a pure best-practice template keyed off the loss-reason taxonomy —
 *      the feature ALWAYS works, and the draft honestly says which path
 *      produced it (`source: 'worker' | 'fallback'`).
 *   3. {@link attachOutcomeContract} — every draft stakes a machine-checkable
 *      before-claim: "this win-back reopens the conversation within 30 days",
 *      with the deal's real dollars at stake (never fabricated).
 *   4. {@link scoreResurrectionOutcome} — replays reality through the
 *      outcome verifier and books revived pipeline to the (injected) value
 *      ledger ONLY when the claim verified. Unknown is never assumed positive.
 *   5. {@link lostLeadResurrectionJob} — the scheduler entry: select → draft
 *      → hand each draft to sequenceEnrollment's approval path as ONE vetoed
 *      pending batch. Nothing here ever sends; a human approves every diff.
 *
 * Deliberately PURE at the module boundary: the worker, the clock, the deal
 * book, the approval queue and the ledger are all injected. No firebase, no
 * config reads, no fabricated customer data — a deal with no captured loss
 * reason drafts against "(no reason given)" honestly.
 */

import type { TimelineEvent } from './recordTimeline';
import {
  buildOutcomeContract,
  type OutcomeContract,
} from '../agent/outcomeContract';
import { verifyOutcome } from '../agent/outcomeVerifier';
import { resolveMerge, type MergeContext } from './mergeFields';
import { scoreDraft } from './contentScore';
import {
  submitEnrollmentForApproval,
  type EnrollmentApprovalQueue,
  type EnrollmentPlan,
} from './sequenceEnrollment';
import type { Sequence } from './outreach';

const DAY_MS = 86_400_000;

// ===========================================================================
// Schema
// ===========================================================================

/** Everything the drafter knows about one resurrectable lost deal. */
export interface LostLeadContext {
  dealId: string;
  accountId: string;
  contactId: string;
  /** The captured winLossReason — "(no reason given)" when never recorded. */
  lossReason: string;
  /** Epoch ms the deal moved to closed-lost. */
  closedLostAt: number;
  /** The record's unified timeline — the grounding the worker reasons over. */
  timeline: TimelineEvent[];
  // -- Optional real-data enrichers (never fabricated when absent) ----------
  /** Contact display name, when the caller knows it (merge rendering). */
  contactName?: string;
  /** Contact email, when known — flows into the enrollment plan. */
  contactEmail?: string;
  /** Account display name, when known (merge rendering). */
  accountName?: string;
  /** USD the lost deal was worth — the dollars staked on the contract. */
  dealValue?: number;
}

/** One drafted win-back touch. Email-only until other channels are governed. */
export interface ResurrectionStep {
  channel: 'email';
  /** Whole days after enrollment this step becomes due (0 = immediately). */
  delayDays: number;
  subject: string;
  body: string;
  /** 0–100 deterministic quality score (crm/contentScore). */
  contentScore: number;
}

/** The full drafted sequence + its sealed before-claim. */
export interface ResurrectionDraft {
  dealId: string;
  steps: ResurrectionStep[];
  outcomeContract: OutcomeContract;
  /** True when the draft demonstrably references the real loss context. */
  grounded: boolean;
  /** Which path produced it — shown as a badge, never hidden. */
  source: 'worker' | 'fallback';
}

// ===========================================================================
// Candidate selection — pure filter over the deal book
// ===========================================================================

/** The loose deal shape the selector consumes (Person/prospect-compatible). */
export interface ResurrectionDeal {
  dealId: string;
  accountId: string;
  contactId: string;
  /** 'closed-lost' | 'closed-won' | anything else = open/in-flight. */
  stage: string;
  lossReason?: string;
  /** Epoch ms of the loss; a deal without one can never age past cooldown. */
  closedLostAt?: number;
  timeline?: TimelineEvent[];
  contactName?: string;
  contactEmail?: string;
  accountName?: string;
  dealValue?: number;
}

const NO_REASON = '(no reason given)';
const CLOSED_STAGES = new Set(['closed-lost', 'closed-won']);

/** Timeline sources that mean "a conversation is already live again". */
const REENGAGEMENT_SOURCES = new Set<TimelineEvent['source']>(['email', 'meeting']);

/** Has anything conversational happened on this record since the loss? */
function reengagedSince(timeline: TimelineEvent[] | undefined, closedLostAt: number): boolean {
  return (timeline ?? []).some(
    (e) => Number.isFinite(e.ts) && e.ts > closedLostAt && REENGAGEMENT_SOURCES.has(e.source),
  );
}

/**
 * Pure filter: which closed-lost deals are honestly resurrectable at `now`?
 *   • closed-lost more than `cooldownDays` ago (a fresh loss needs space);
 *   • NOT re-engaged — no email/meeting on the timeline since the loss;
 *   • no other OPEN deal on the same account (that owner runs the account).
 * Deals that don't carry a finite `closedLostAt` never qualify — we never
 * guess when a deal died.
 */
export function selectResurrectionCandidates(
  deals: ResurrectionDeal[],
  now: number,
  cooldownDays: number,
): LostLeadContext[] {
  const busyAccounts = new Set(
    deals.filter((d) => !CLOSED_STAGES.has(d.stage)).map((d) => d.accountId),
  );
  const cooldownMs = cooldownDays * DAY_MS;

  return deals
    .filter((d) => {
      if (d.stage !== 'closed-lost') return false;
      if (typeof d.closedLostAt !== 'number' || !Number.isFinite(d.closedLostAt)) return false;
      if (now - d.closedLostAt < cooldownMs) return false;
      if (reengagedSince(d.timeline, d.closedLostAt)) return false;
      if (busyAccounts.has(d.accountId)) return false;
      return true;
    })
    .map((d) => ({
      dealId: d.dealId,
      accountId: d.accountId,
      contactId: d.contactId,
      lossReason: d.lossReason?.trim() || NO_REASON,
      closedLostAt: d.closedLostAt as number,
      timeline: d.timeline ?? [],
      ...(d.contactName !== undefined ? { contactName: d.contactName } : {}),
      ...(d.contactEmail !== undefined ? { contactEmail: d.contactEmail } : {}),
      ...(d.accountName !== undefined ? { accountName: d.accountName } : {}),
      ...(d.dealValue !== undefined ? { dealValue: d.dealValue } : {}),
    }));
}

// ===========================================================================
// Outcome contract — the 30-day reopen claim
// ===========================================================================

/** The reopen window every resurrection stakes: 30 days. */
export const REOPEN_WINDOW_MS = 30 * DAY_MS;

/** The action class resurrection contracts ride on. */
export const RESURRECTION_ACTION_TYPE = 'lost_lead_resurrection';

/** A draft before (or after) its contract is stamped. */
type DraftSansContract = Omit<ResurrectionDraft, 'outcomeContract'> &
  Partial<Pick<ResurrectionDraft, 'outcomeContract'>>;

/**
 * Stamp (or re-stamp) the before-claim onto a draft: "this win-back gets a
 * reply — the conversation reopens — within 30 days", with the deal's real
 * dollars at stake. Pure; pass `now` for determinism (defaults to the clock).
 */
export function attachOutcomeContract(
  draft: DraftSansContract,
  opts: { now?: number; dollarsAtStake?: number } = {},
): ResurrectionDraft {
  const now = opts.now ?? Date.now();
  const dollars =
    opts.dollarsAtStake ?? draft.outcomeContract?.dollarsAtStake ?? 0;
  const contract = buildOutcomeContract(
    {
      id: `resurrection:${draft.dealId}`,
      action: { kind: RESURRECTION_ACTION_TYPE, value: dollars },
    },
    { kind: 'reply_received', now, windowMs: REOPEN_WINDOW_MS, dollarsAtStake: dollars },
  );
  return { ...draft, outcomeContract: contract } as ResurrectionDraft;
}

// ===========================================================================
// Deterministic fallback drafter — pure, always available
// ===========================================================================

/**
 * Loss-reason → the honest re-engagement angle the fallback leads with.
 * Keyed off the LOST_REASONS taxonomy (crm/winloss.ts); anything else —
 * including "(no reason given)" — gets the generic closing-the-loop angle.
 */
const LOSS_ANGLES: Record<string, string> = {
  'Price too high': 'our packaging has changed since we last spoke — there are smaller entry points now',
  'Lost to competitor': 'teams often compare notes once the first renewal comes around',
  'No budget': 'new budget cycles have a way of reopening doors',
  'No decision / stalled': 'picking a stalled thread back up is far easier than starting over',
  'Bad timing': 'timing may finally be on our side',
  'Missing feature': 'the gap you flagged then has been squarely on our roadmap since',
  'No champion': 'happy to make the case directly to whoever owns this now',
  'Went silent': 'closing the loop either way — a quick no is genuinely welcome',
};

const GENERIC_ANGLE = 'a lot has changed on our side since we last spoke';

/** Merge context built ONLY from what the caller really knows. */
function mergeCtxOf(ctx: LostLeadContext): MergeContext {
  return {
    account: {
      ...(ctx.accountName !== undefined ? { name: ctx.accountName } : {}),
    },
    contacts: [
      {
        ...(ctx.contactName !== undefined ? { name: ctx.contactName } : {}),
        ...(ctx.contactEmail !== undefined ? { email: ctx.contactEmail } : {}),
      },
    ],
  };
}

/** Render one templated step through the governed merge registry + score it. */
function renderStep(
  delayDays: number,
  subjectTpl: string,
  bodyTpl: string,
  merge: MergeContext,
): ResurrectionStep {
  const subject = resolveMerge(subjectTpl, merge).text;
  const body = resolveMerge(bodyTpl, merge).text;
  return {
    channel: 'email',
    delayDays,
    subject,
    body,
    contentScore: scoreDraft(`${subject}\n${body}`).total,
  };
}

/**
 * The always-available drafter: a best-practice 3-touch win-back templated
 * around the REAL captured loss reason, rendered through the merge-field
 * registry (fallbacks, never "undefined"). Pure — pass `now` to pin the
 * contract window in tests. `grounded` is true only when a real loss reason
 * exists to ground on.
 */
export function deterministicDraft(ctx: LostLeadContext, now?: number): ResurrectionDraft {
  const reason = ctx.lossReason.trim() || NO_REASON;
  const angle = LOSS_ANGLES[reason] ?? GENERIC_ANGLE;
  const merge = mergeCtxOf(ctx);

  const steps: ResurrectionStep[] = [
    renderStep(
      0,
      'Picking things back up, {{contact.name}}',
      `Hi {{contact.name}},\n\nWhen we last spoke, the sticking point was "${reason}" — and ${angle}. ` +
        `Rather than restart from zero, would a 15-minute catch-up be worth it for {{account.name}}?\n\n— {{owner.name}}`,
      merge,
    ),
    renderStep(
      4,
      'One concrete change since we spoke',
      `Hi {{contact.name}},\n\nFollowing up on my last note — the specific thing that stalled us ("${reason}") ` +
        `is exactly what has moved since. Happy to share a two-line summary of what's different for {{account.name}} before you commit any time.`,
      merge,
    ),
    renderStep(
      9,
      'Closing the loop',
      `Hi {{contact.name}},\n\nLast note from me. If "${reason}" still holds, a quick no is genuinely useful ` +
        `and I'll close this out. If it doesn't, I'd love to pick the thread back up whenever suits {{account.name}}.\n\n— {{owner.name}}`,
      merge,
    ),
  ];

  return attachOutcomeContract(
    {
      dealId: ctx.dealId,
      steps,
      grounded: reason !== NO_REASON,
      source: 'fallback',
    },
    { ...(now !== undefined ? { now } : {}), dollarsAtStake: ctx.dealValue ?? 0 },
  );
}

// ===========================================================================
// Worker path — the real agentic drafter with graceful degradation
// ===========================================================================

/**
 * The seam to the real backend. `import * as argoWorker` from
 * src/lib/agent/argoWorker satisfies this structurally, so the UI passes the
 * live module while tests pass a stub — no firebase touches this file.
 */
export interface ResurrectionWorker {
  isConfigured(): boolean;
  runAgent(
    agent: { id: string; name: string; template: string; persona: string },
    snapshot: unknown,
  ): Promise<{ text: string }>;
}

/** The agent descriptor the worker runs for win-back drafting. */
export const RESURRECTION_AGENT = {
  id: 'lost-lead-resurrection',
  name: 'Lost-Lead Resurrection Drafter',
  template: 'winback-drafter',
  persona:
    'You draft honest, low-pressure win-back email sequences for closed-lost deals. ' +
    'Ground every step in the captured loss reason and the record timeline. ' +
    'Return ONLY a JSON array of exactly 3 steps: ' +
    '[{"delayDays":number,"subject":string,"body":string}]. Never invent facts.',
} as const;

/** The grounding snapshot the worker reasons over — real data only. */
function workerSnapshot(ctx: LostLeadContext): unknown {
  return {
    task: 'draft_winback_sequence',
    dealId: ctx.dealId,
    lossReason: ctx.lossReason,
    closedLostAt: new Date(ctx.closedLostAt).toISOString(),
    ...(ctx.accountName !== undefined ? { accountName: ctx.accountName } : {}),
    ...(ctx.contactName !== undefined ? { contactName: ctx.contactName } : {}),
    ...(ctx.dealValue !== undefined ? { dealValue: ctx.dealValue } : {}),
    timeline: ctx.timeline.slice(0, 20).map((e) => ({
      ts: new Date(e.ts).toISOString(),
      source: e.source,
      summary: e.summary,
    })),
  };
}

/** Parse + shape-check the worker's reply into steps; [] when malformed. */
function parseWorkerSteps(text: string): Array<{ delayDays: number; subject: string; body: string }> {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    // The model may wrap the JSON in prose — salvage the first array literal.
    const m = text.match(/\[[\s\S]*\]/);
    if (!m) return [];
    try {
      raw = JSON.parse(m[0]);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(raw)) return [];
  const steps: Array<{ delayDays: number; subject: string; body: string }> = [];
  for (const item of raw.slice(0, 3)) {
    if (typeof item !== 'object' || item === null) continue;
    const o = item as Record<string, unknown>;
    const subject = typeof o.subject === 'string' ? o.subject.trim() : '';
    const body = typeof o.body === 'string' ? o.body.trim() : '';
    if (subject === '' || body === '') continue;
    const delayDays =
      typeof o.delayDays === 'number' && Number.isFinite(o.delayDays) && o.delayDays >= 0
        ? Math.round(o.delayDays)
        : steps.length * 4;
    steps.push({ delayDays, subject, body });
  }
  return steps;
}

/**
 * Draft a win-back sequence for one candidate. Prefers the REAL injected
 * worker (grounded reasoning over lossReason + timeline); ANY failure —
 * unconfigured, thrown error, timeout surfaced as an error, malformed
 * reply — degrades to {@link deterministicDraft}. The feature never breaks;
 * the draft's `source` badge tells the operator which path ran.
 */
export async function draftResurrectionSequence(
  ctx: LostLeadContext,
  worker?: ResurrectionWorker | null,
): Promise<ResurrectionDraft> {
  if (!worker || typeof worker.runAgent !== 'function') return deterministicDraft(ctx);
  try {
    if (typeof worker.isConfigured === 'function' && !worker.isConfigured()) {
      return deterministicDraft(ctx);
    }
    const { text } = await worker.runAgent(RESURRECTION_AGENT, workerSnapshot(ctx));
    const parsed = parseWorkerSteps(text);
    if (parsed.length === 0) return deterministicDraft(ctx);

    const steps: ResurrectionStep[] = parsed.map((s) => ({
      channel: 'email',
      delayDays: s.delayDays,
      subject: s.subject,
      body: s.body,
      contentScore: scoreDraft(`${s.subject}\n${s.body}`).total,
    }));
    // Grounded only when the copy demonstrably references the real loss
    // reason — we verify the claim rather than trusting the model.
    const needle = ctx.lossReason.toLowerCase();
    const grounded =
      ctx.lossReason !== NO_REASON &&
      steps.some((s) => `${s.subject} ${s.body}`.toLowerCase().includes(needle));

    return attachOutcomeContract(
      { dealId: ctx.dealId, steps, grounded, source: 'worker' },
      { dollarsAtStake: ctx.dealValue ?? 0 },
    );
  } catch {
    return deterministicDraft(ctx);
  }
}

// ===========================================================================
// Outcome scoring — revived pipeline booked ONLY when verified
// ===========================================================================

/** The only ledger capability this module needs — trivially fakeable. */
export interface RevivedPipelineLedger {
  book(entry: { dealId: string; dollars: number }): void;
}

/**
 * Close the loop on one draft: replay the recorded reality (did a reply
 * land?) through the outcome verifier against the sealed 30-day contract,
 * and book the reopened deal's value to the ledger ONLY when the verdict is
 * VERIFIED and real dollars reopened. No reply, or a $0 reopen, books
 * nothing — revived pipeline is attributed, never fabricated.
 */
export function scoreResurrectionOutcome(
  draft: ResurrectionDraft,
  replied: boolean,
  reopenedDealValue: number,
  ledger: RevivedPipelineLedger,
): void {
  const { window } = draft.outcomeContract.predicate;
  const activity = replied
    ? [{ kind: 'reply_received', at: window.start, id: `reply:${draft.dealId}` }]
    : [];
  const result = verifyOutcome(draft.outcomeContract, {
    activity,
    healthAt: () => undefined,
    now: window.end,
  });
  if (result.verdict === 'VERIFIED' && reopenedDealValue > 0) {
    ledger.book({ dealId: draft.dealId, dollars: reopenedDealValue });
  }
}

// ===========================================================================
// Approval-path adapter + scheduler job — never auto-sends
// ===========================================================================

/** A draft's steps as a Sequence the enrollment engine understands. */
export function resurrectionDraftToSequence(draft: ResurrectionDraft): Sequence {
  return {
    id: `resurrection:${draft.dealId}`,
    name: `Win-back — deal ${draft.dealId}`,
    steps: draft.steps.map((s, i) => ({
      id: `winback-step-${i + 1}`,
      dayOffset: s.delayDays,
      channel: 'email' as const,
      subject: s.subject,
      body: s.body,
    })),
  };
}

/**
 * Hand ONE draft to sequenceEnrollment's governed approval path: a
 * single-contact plan, submitted as a vetoed pending batch of rendered send
 * diffs. This is the ONLY exit a draft has — there is no send call anywhere
 * in this module.
 */
export function submitResurrectionForApproval(
  draft: ResurrectionDraft,
  ctx: LostLeadContext,
  queue: EnrollmentApprovalQueue,
): { batchId: string; itemCount: number } {
  const sequence = resurrectionDraftToSequence(draft);
  const email = ctx.contactEmail ?? '';
  const plan: EnrollmentPlan = {
    sequenceId: sequence.id,
    enrolled: [
      {
        contactId: ctx.contactId,
        email,
        accountId: ctx.accountId,
        sequenceId: sequence.id,
        mergeContext: mergeCtxOf(ctx) as Record<string, unknown>,
      },
    ],
    suppressed: [],
    blastRadius: {
      contacts: 1,
      accounts: 1,
      domains: email.includes('@') ? 1 : 0,
    },
  };
  return submitEnrollmentForApproval(plan, queue, sequence);
}

/** Everything the job needs, injected — no store, no firebase, no config. */
export interface ResurrectionJobDeps {
  /** The live deal book (the caller derives it from real records). */
  deals: ResurrectionDeal[];
  /** The governed approval queue drafts are handed to. */
  queue: EnrollmentApprovalQueue;
  /** The real argoWorker module (or a stub); null/absent = fallback drafts. */
  worker?: ResurrectionWorker | null;
  /** Injected clock (epoch ms); wall clock when omitted. */
  now?: number;
  /** Days a loss must age before resurrection. Default 30. */
  cooldownDays?: number;
}

export interface ResurrectionJobResult {
  /** How many deals cleared the candidate filter. */
  candidates: number;
  drafts: ResurrectionDraft[];
  /** One entry per draft handed to the approval path. */
  submitted: Array<{ dealId: string; batchId: string; itemCount: number; source: 'worker' | 'fallback' }>;
}

/** Default cooldown before a lost deal becomes resurrectable. */
export const DEFAULT_COOLDOWN_DAYS = 30;

/**
 * One full job run: select candidates → draft each (worker-first, fallback
 * always) → submit every draft to the approval path as a vetoed pending
 * batch. Returns an honest report of what was drafted and where it went.
 */
export async function runLostLeadResurrectionJob(
  deps: ResurrectionJobDeps,
): Promise<ResurrectionJobResult> {
  const now = deps.now ?? Date.now();
  const candidates = selectResurrectionCandidates(
    deps.deals,
    now,
    deps.cooldownDays ?? DEFAULT_COOLDOWN_DAYS,
  );

  const drafts: ResurrectionDraft[] = [];
  const submitted: ResurrectionJobResult['submitted'] = [];
  for (const ctx of candidates) {
    const draft = await draftResurrectionSequence(ctx, deps.worker ?? null);
    const { batchId, itemCount } = submitResurrectionForApproval(draft, ctx, deps.queue);
    drafts.push(draft);
    submitted.push({ dealId: draft.dealId, batchId, itemCount, source: draft.source });
  }

  return { candidates: candidates.length, drafts, submitted };
}

/**
 * The scheduler job entry: a named, cadenced unit of agent work the
 * scheduler loop (or a deployed agent's run) drives. `run` is the whole
 * loop — select, draft, submit-for-approval — and never sends anything.
 */
export const lostLeadResurrectionJob = {
  id: 'lost-lead-resurrection',
  name: 'Lost-Lead Resurrection',
  cadence: 'daily' as const,
  run: runLostLeadResurrectionJob,
};
